import { Rect } from "../../drawing";
import { ChantContext } from "../../gregorio-chant/ChantContext";
import { ChantSyllableElement } from "./ChantSyllableElement";
import { ChantLayoutElement } from "../layout/ChantLayoutElement";
import { Custos } from "../signs";
import { DropCap } from "../texts/DropCap";
import { Annotation } from "../texts/Annotation";
import { calculateHeightPositionFromPitch } from "../utils";
import { LedgerLine } from "./LegderLine";
import { GregorioWordPosition } from "../../gregorio-chant/structures/GregorioSyllable";
import { ChantSegmentElement } from "./ChantSegmentElement";
import { findLast, max, maxBy, min, minBy, sumBy } from "lodash";

export class ChantLine extends ChantLayoutElement {
  private _staffLeft = 0;
  private _staffRight = 0;
  private _isOneLineMode = false;

  private _custos: Custos | undefined;
  private _syllables: ChantSyllableElement[] = [];

  private _ledgerLines: LedgerLine[] = [];

  private _dropCap: DropCap | undefined;
  private _annotation: Annotation | undefined;

  get width() {
    return this._staffRight;
  }

  get bottom() {
    return this.bounds.bottom + this._context.lyricTextSizePx;
  }

  get hasNabc() {
    return this._syllables.some((s) => s.hasNabc);
  }

  get hasAboveLineText() {
    return this._syllables.some((s) => s.hasAboveLineText);
  }

  get top() {
    return min(this._syllables.map((s) => s.top)) || 0;
  }

  private _interwordspacetext = 0;
  private _interwordspacenotes = 0;

  constructor(context: ChantContext, dropCap?: DropCap, annotation?: Annotation) {
    super(context);
    this._dropCap = dropCap;
    this._annotation = annotation;
    this._staffRight = context.lineWidthPx;
    this._isOneLineMode = this._context.lineWidthPx < 1;
    this._interwordspacetext = this._distanceConverter.lengthToPx(this._context.interwordspacetext);
    this._interwordspacenotes = this._distanceConverter.lengthToPx(
      this._context.interwordspacenotes
    );
  }

  // returns the number of notations added
  public performLayout(
    allNotationElements: ChantSyllableElement[],
    startingIndex: number
  ): number {
    this.addDropCapOrAnnotationPadding();

    const defaultHeight = this._context.nabcOnlyMode
      ? this._context.nabcLineHeight
      : 6 * this._context.staffLinesInterval;
    let notationBounds = Rect.create(
      this._staffLeft,
      0,
      this._staffRight - this._staffLeft,
      defaultHeight
    );
    let baselineBounds = notationBounds.clone();

    const lastClefSyllable = findLast(
      allNotationElements.slice(0, startingIndex),
      (n) => n.hasClef
    );
    if (startingIndex > 0 && lastClefSyllable) {
      this.addStartingClef(allNotationElements, lastClefSyllable, startingIndex, notationBounds);
    }

    const currentIndex = this.fillNotations(allNotationElements, startingIndex, notationBounds);

    if (!this._context.nabcOnlyMode) {
      this.fillLedgerLines();

      if (this._syllables.length > 0) {
        const topElement = minBy(this._syllables.map((s) => s.segments).flat(), (s) => s.maxY);
        if (topElement) {
          notationBounds = notationBounds.union(
            Rect.create(this._staffLeft, topElement.maxY, this._staffRight - this._staffLeft, 0)
          );
        }
      }

      if (this._ledgerLines.length > 0) {
        const minLedger = minBy(this._ledgerLines, (l) => l.notePitch);
        const maxLedger = maxBy(this._ledgerLines, (l) => l.notePitch);

        if (minLedger) {
          const minrect = Rect.create(
            this._staffLeft,
            calculateHeightPositionFromPitch(this._context, minLedger.notePitch),
            this._staffRight - this._staffLeft,
            0
          );
          notationBounds = notationBounds.union(minrect);

          if (minLedger.pitch < 7) {
            baselineBounds = baselineBounds.union(minrect);
          }
        }

        if (maxLedger) {
          const maxrect = Rect.create(
            this._staffLeft,
            calculateHeightPositionFromPitch(this._context, maxLedger.notePitch),
            this._staffRight - this._staffLeft,
            0
          );
          notationBounds = notationBounds.union(maxrect);
        }
      }
    }

    this._syllables.forEach((n) => (notationBounds = notationBounds.union(n.bounds)));

    if (this._dropCap) {
      this._dropCap.performLayout(this._staffLeft, notationBounds);
    }

    if (this._annotation) {
      this._annotation.performLayout(this._staffLeft);
    }

    let nabcHeight = 0;
    if (this.hasNabc) {
      nabcHeight =
        this._context.nabcLineHeight +
        this._distanceConverter.lengthToPx(this._context.abovelinestextraise) +
        this._distanceConverter.lengthToPx(this._context.abovelinestextheight);
    }

    const spacelinestext = this._distanceConverter.lengthToPx(this._context.spacelinestext);
    const spacebeneathtext = this._distanceConverter.lengthToPx(this._context.spacebeneathtext);

    for (const syl of this._syllables) {
      syl.lyric?.bounds.setY(notationBounds.bottom + spacelinestext);
    }

    this.bounds = Rect.create(
      0,
      Math.max(-notationBounds.y, nabcHeight),
      notationBounds.right,
      baselineBounds.height + spacelinestext + spacebeneathtext
    );

    return currentIndex - startingIndex;
  }

  public createInteractiveSvgFragments(): SVGElement[] {
    const rootElement = document.createElementNS("http://www.w3.org/2000/svg", "g");
    rootElement.setAttribute("class", "chantLineInteractive");
    rootElement.setAttribute("transform", "translate(" + this.bounds.x + "," + this.bounds.y + ")");

    for (const syllable of this._syllables) {
      rootElement.appendChild(syllable.createRect());
    }

    return [rootElement];
  }

  public createSvgFragments(): SVGElement[] {
    const x1 = this._staffLeft,
      x2 = this._staffRight;

    // container
    const rootElement = document.createElementNS("http://www.w3.org/2000/svg", "g");
    rootElement.setAttribute("class", "ChantLine");
    rootElement.setAttribute("transform", "translate(" + this.bounds.x + "," + this.bounds.y + ")");

    const staffLinesRoot = document.createElementNS("http://www.w3.org/2000/svg", "g");
    staffLinesRoot.setAttribute("class", "ChantLineInner");

    if (!this._context.nabcOnlyMode) {
      // custos
      if (this._custos) {
        for (const fragment of this._custos.createSvgFragments()) {
          staffLinesRoot.appendChild(fragment);
        }
      }

      if (this.hasNabc) {
        // const baseline =
        //   this._distanceConverter.lengthToPx(this._context.abovelinestextraise) +
        //   this._distanceConverter.lengthToPx(this._context.abovelinestextheight) +
        //   this._context.nabcLineHeight;
        // staffLinesRoot.setAttribute("transform", `translate(0, ${baseline})`);
      }
      // staff lines
      for (let i = 0; i <= 6; i = i + 2) {
        const staffLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
        staffLine.setAttribute("x1", String(x1));
        staffLine.setAttribute("y1", String(this._context.staffLinesInterval * i));
        staffLine.setAttribute("x2", String(x2));
        staffLine.setAttribute("y2", String(this._context.staffLinesInterval * i));
        staffLine.setAttribute("stroke", this._context.staffLineColor);
        staffLine.setAttribute("stroke-width", String(this._context.staffLineWeight));
        staffLine.setAttribute("class", "StaffLine");

        staffLinesRoot.appendChild(staffLine);
      }

      // ledger lines
      for (let i = 0; i < this._ledgerLines.length; i++) {
        const ledgerLine = this._ledgerLines[i];
        const y = calculateHeightPositionFromPitch(this._context, ledgerLine.pitch);

        const ledgerLineEl = document.createElementNS("http://www.w3.org/2000/svg", "line");
        ledgerLineEl.setAttribute("x1", String(ledgerLine.x1));
        ledgerLineEl.setAttribute("y1", String(y));
        ledgerLineEl.setAttribute("x2", String(ledgerLine.x2));
        ledgerLineEl.setAttribute("y2", String(y));
        ledgerLineEl.setAttribute("stroke", this._context.staffLineColor);
        ledgerLineEl.setAttribute("stroke-width", String(this._context.staffLineWeight));
        ledgerLineEl.setAttribute("class", "LedgerLine");

        staffLinesRoot.appendChild(ledgerLineEl);
      }
    }

    // dropCap
    if (this._dropCap) {
      for (const fragment of this._dropCap.createSvgFragments()) {
        staffLinesRoot.appendChild(fragment);
      }
    }

    // annotation
    if (this._annotation) {
      for (const fragment of this._annotation.createSvgFragments()) {
        rootElement.appendChild(fragment);
      }
    }

    // syllables
    this._syllables.forEach((syllable, index) => {
      syllable.isLastSyllable = index === this._syllables.length - 1;
      for (const fragment of syllable.createSvgFragments()) {
        staffLinesRoot.appendChild(fragment);
      }
    });

    rootElement.appendChild(staffLinesRoot);

    return [rootElement, ...super.createSvgFragments()];
  }

  private fillLedgerLines(): void {
    this._ledgerLines = [];

    this._syllables.forEach((syllable) => {
      syllable.segments.forEach((segment) => {
        segment.noteVisualizers.forEach((noteGlyph, index) => {
          noteGlyph.notes.forEach((note) => {
            if (!note.needsLedgerLine) {
              return;
            }

            const x1 =
              syllable.bounds.x + segment.bounds.x + noteGlyph.bounds.x - this._context.pitchWidth;
            let x2 = x1 + noteGlyph.bounds.width + this._context.pitchWidth * 2;

            if (x2 > this._staffRight) {
              x2 = this._staffRight;
            }

            const prevGlyph = segment.noteVisualizers[index - 1];

            if (
              prevGlyph &&
              prevGlyph.notes.some((n) => n.ledgerLinePitch === note.ledgerLinePitch)
            ) {
              const lastLedger = this._ledgerLines[this._ledgerLines.length - 1];
              lastLedger.setX2(x2);
            } else {
              this._ledgerLines.push(new LedgerLine(x1, x2, note.ledgerLinePitch, note.pitch));
            }
          });
        });
      });
    });
  }

  private fillNotations(
    allSyllables: ChantSyllableElement[],
    startingIndex: number,
    notationBounds: Rect
  ): number {
    const syllables: ChantSyllableElement[] = [];
    let currIndex = startingIndex;
    let currSyl: ChantSyllableElement = allSyllables[startingIndex];
    const prev: ChantSyllableElement[] = [];

    let keepTogetherStack = [];

    while (currIndex < allSyllables.length) {
      const prevSyl = allSyllables[currIndex - 1];
      // const nextSyl = allSyllables[currIndex + 1];

      currSyl = allSyllables[currIndex];
      currSyl.performLayout(notationBounds);

      if (currSyl.keepWithNext) {
        keepTogetherStack.push(currSyl);
      } else if (prevSyl?.keepWithNext === false) {
        keepTogetherStack = [];
      }

      const positioningResult = this.horizontallyPositionNotationElement(currSyl, prev);

      if (currSyl.hasCustos) {
        currSyl.getCustosSegment?.custos?.setHeightFromNotations(allSyllables, currIndex);
      }

      // if (nextSyl && nextSyl.isBarFirst) {
      //   nextSyl.performLayout(notationBounds);
      //   const barPositioningResult = this.horizontallyPositionNotationElement(nextSyl, [
      //     ...prev,
      //     currSyl,
      //   ]);
      //   if (barPositioningResult.needBreakBefore) {
      //     this.addCustos(allSyllables, currIndex, notationBounds);
      //     ++currIndex;
      //     break;
      //   }
      // }

      if (positioningResult.needBreakBefore) {
        if (currSyl.isBarFirst) {
          syllables.pop();

          const prevBreakpoints = prevSyl.getBreakpoints();
          if (prevBreakpoints.length > 0) {
            const lastBreadkpoint = prevBreakpoints[prevBreakpoints.length - 1];
            const [firstSyllable, secondSyllable] = prevSyl.split(
              lastBreadkpoint.segmentIndex,
              lastBreadkpoint.visualizerIndex - 1
            );

            if (firstSyllable) {
              firstSyllable.performLayout(notationBounds);
              const result = this.horizontallyPositionNotationElement(
                firstSyllable,
                prev.slice(0, prev.length - 1)
              );
              firstSyllable.bounds.setX(result.x);
              syllables.push(firstSyllable);
              allSyllables.splice(currIndex - 1, 1, firstSyllable);

              if (secondSyllable) {
                allSyllables.splice(currIndex, 0, secondSyllable);
              }
            }
          } else {
            currIndex--;
          }

          this.addCustos(allSyllables, currIndex, notationBounds);
          break;
        }

        if (keepTogetherStack.length > 0) {
          for (const syl of keepTogetherStack) {
            const sylIndex = syllables.findIndex((s) => s.index === syl.index);
            if (sylIndex > -1) {
              syllables.splice(sylIndex, 1);
              currIndex -= 1;
            }
          }
        } else {
          const breakpoints = currSyl.getBreakpoints();
          const spaceLeftPx = currSyl.bounds.width - positioningResult.overflowPx;

          if (breakpoints.length > 0) {
            let breakpointIndex = 0;
            for (; breakpointIndex < breakpoints.length; breakpointIndex++) {
              if (breakpoints[breakpointIndex].right > spaceLeftPx) {
                break;
              }
            }
            breakpointIndex--;

            if (breakpointIndex > -1 && breakpointIndex < breakpoints.length) {
              const targetBreakpoint = breakpoints[breakpointIndex];
              const [firstSyllable, secondSyllable] = currSyl.split(
                targetBreakpoint.segmentIndex,
                targetBreakpoint.visualizerIndex
              );

              if (firstSyllable) {
                firstSyllable.performLayout(notationBounds);
                const result = this.horizontallyPositionNotationElement(firstSyllable, prev);
                firstSyllable.bounds.setX(result.x);
                syllables.push(firstSyllable);
                allSyllables.splice(currIndex, 1, firstSyllable);
                currIndex++;

                if (secondSyllable) {
                  allSyllables.splice(currIndex, 0, secondSyllable);
                }
              }
            }
          }
        }

        if (currIndex <= startingIndex + 1) {
          return currIndex + 1;
        }

        this.addCustos(allSyllables, currIndex, notationBounds);
        break;
      } else if (currSyl.hasEndOfLine && !this._isOneLineMode) {
        syllables.push(currSyl);

        if (currIndex < allSyllables.length - 1) {
          this.addCustos(allSyllables, currIndex + 1, notationBounds);
        }

        if (!currSyl.endOfLineInfo?.eol_ragged) {
          this.justifyNotations(syllables);
        }
        currIndex++;

        break;
      }

      syllables.push(currSyl);
      prev.push(currSyl);

      currIndex++;
    }

    if (this._isOneLineMode) {
      this._staffRight = syllables[syllables.length - 1].bounds.right;
    }

    this._syllables = syllables;
    return currIndex;
  }

  private addCustos(notes: ChantSyllableElement[], index: number, notationBounds: Rect) {
    const custos = new Custos(this._context);
    custos.performLayout();
    custos.setX(notationBounds.right);
    custos.setHeightFromNotations(notes, index);
    this._custos = custos;
  }

  private addDropCapOrAnnotationPadding() {
    if (!this._dropCap && !this._annotation) {
      return;
    }

    let dropCapPadding = 0;
    const annotationPadding = 0;

    if (this._dropCap) {
      dropCapPadding = this._dropCap.bounds.width + this._dropCap.padding * 2;
    }

    if (this._annotation) {
      // annotationPadding = this._annotation.bounds.width + this._annotation.padding * 4;
    }

    this._staffLeft += Math.max(dropCapPadding, annotationPadding);
  }

  private addStartingClef(
    allNotationElements: ChantSyllableElement[],
    lastClefSyllable: ChantSyllableElement,
    startingIndex: number,
    notationBounds: Rect
  ) {
    const clefSyl = new ChantSyllableElement(this._context, lastClefSyllable.gregorioSyllable);
    const clefSeg = lastClefSyllable.segments.find((seg) => seg.hasClef);
    const newSeg = new ChantSegmentElement(this._context, clefSeg!.segment);
    const newClef = clefSeg!.clef.copy();
    newClef.bounds.setX(0);
    newSeg.addVisualizer(newClef);
    clefSyl.addSegments([newSeg]);
    clefSyl.performLayout(notationBounds);
    allNotationElements.splice(startingIndex, 0, clefSyl);
  }

  // eslint-disable-next-line max-len
  private calculateIntersyllableSpaceWhenPrevHasLyrics(
    prevRight: number,
    prevLyricsRight: number,
    currLyricsX: number
  ) {
    const calculated = this.trailingSpace(prevRight, prevLyricsRight);
    if (prevLyricsRight > prevRight) {
      return calculated - currLyricsX;
    } else {
      if (prevLyricsRight) {
        return calculated - currLyricsX;
      } else {
        return calculated;
      }
    }
  }

  // eslint-disable-next-line max-len
  private calculateXForFirstSyllable(
    curr: ChantSyllableElement,
    prev: ChantSyllableElement,
    currLyricsX: number,
    prevRight: number,
    prevLyricsRight: number
  ): number {
    if ((prev && prev.hasLyric && curr.hasLyric && !prev.isClef) || this._context.nabcOnlyMode) {
      return this.calculateIntersyllableSpaceWhenPrevHasLyrics(
        prevRight,
        prevLyricsRight,
        currLyricsX
      );
    } else {
      const targetX = this.trailingSpace(prevRight, prevLyricsRight);
      const lyricsOverlap = prevRight - prevLyricsRight;
      if (lyricsOverlap > 0) {
        const diff = lyricsOverlap + currLyricsX;
        if (diff < 0) {
          return targetX - diff + this._interwordspacetext;
        } else {
          return targetX;
        }
      }
      return targetX - currLyricsX;
    }
  }

  private handleHyphen(prev: ChantSyllableElement, currLyricsX: number): number {
    let prevRightMostLyricsPoint = prev.bounds.x + prev.lyric!.bounds.right;
    const prevRightMostNotationPoint = prev.bounds.right;
    // eslint-disable-next-line max-len
    const diff =
      prevRightMostNotationPoint > prevRightMostLyricsPoint
        ? prevRightMostNotationPoint - prevRightMostLyricsPoint - currLyricsX
        : 0;
    const maximumspacewithoutdash = this._distanceConverter.lengthToPx(
      this._context.maximumspacewithoutdash
    );

    if (prevRightMostNotationPoint > prevRightMostLyricsPoint || diff > maximumspacewithoutdash) {
      prev.lyric!.addConnector();
      prevRightMostLyricsPoint = prev.bounds.x + prev.lyric!.bounds.right;
      return Math.max(
        prevRightMostLyricsPoint - currLyricsX,
        prevRightMostNotationPoint + this._interwordspacenotes
      );
    } else {
      const diff2 = prevRightMostLyricsPoint - prevRightMostNotationPoint - currLyricsX;
      if (diff2 < this._interwordspacenotes) {
        prev.lyric!.addConnector();
        prevRightMostLyricsPoint = prev.bounds.x + prev.lyric!.bounds.right;
        return (
          Math.max(
            prevRightMostLyricsPoint,
            prevRightMostNotationPoint + this._interwordspacenotes
          ) - currLyricsX
        );
      }
      return prevRightMostLyricsPoint - currLyricsX;
    }
  }

  private horizontallyPositionNotationElement(
    // eslint-disable-next-line max-len
    curr: ChantSyllableElement,
    prevArr: ChantSyllableElement[]
  ): { needBreakBefore: boolean; overflowPx: number; x: number } {
    const prev = prevArr[prevArr.length - 1];
    // eslint-disable-next-line max-len
    const prevRight = prev?.bounds.right || 0;
    // eslint-disable-next-line max-len
    const rightLyrics = prevArr.map((prev) =>
      (prev?.lyric?.bounds.right || 0) > 0 ? prev?.bounds.x + (prev?.lyric?.bounds.right || 0) : 0
    );
    const prevLyricsRight = max(rightLyrics) || 0;

    if (!prev) {
      curr.bounds.setX(this._staffLeft - (curr.lyric?.bounds.x || 0));
      return { needBreakBefore: false, overflowPx: 0, x: this._staffLeft };
    }

    let x = prevRight;
    const currLyricsX = curr.lyric?.bounds.x || 0;

    if (prev.isClef) {
      x = this.trailingSpace(prev.bounds.right, 0) - currLyricsX;
    } else if (
      curr.gregorioSyllable?.position === GregorioWordPosition.WORD_ONE_SYLLABLE ||
      curr.gregorioSyllable?.position === GregorioWordPosition.BEGINNING
    ) {
      x = this.calculateXForFirstSyllable(curr, prev, currLyricsX, prevRight, prevLyricsRight);
    } else if (prev && prev.hasLyric) {
      x = this.handleHyphen(prev, currLyricsX);
    } else {
      x = this.calculateIntersyllableSpaceWhenPrevHasLyrics(
        prevRight,
        prevLyricsRight,
        currLyricsX
      );
    }

    // special case for alternation
    const firstSegmentX = curr.segments[0].firstVisualizer?.bounds.x || 0;
    if (firstSegmentX < 0) {
      const diff = prevRight - prevLyricsRight;
      if (diff > 0 || Math.abs(diff) < Math.abs(firstSegmentX)) {
        x -= firstSegmentX;
      }
    }

    const overflowPx =
      x + curr.bounds.width - (this._staffRight - this._context.staffLineRightPaddingPx);

    if (!this._isOneLineMode && overflowPx > 0) {
      return { needBreakBefore: true, overflowPx, x };
    }

    curr.bounds.setX(x);

    return { needBreakBefore: false, overflowPx: 0, x };
  }

  private trailingSpace(noteX: number, textX: number) {
    if (noteX > textX) {
      return noteX + this._interwordspacenotes;
    }
    return textX + this._interwordspacetext;
  }

  private justifyNotations(notations: ChantSyllableElement[]) {
    if (notations.length < 3) {
      return;
    }
    const justificationNotations = notations.slice(2);
    const custosWidth = this._custos ? this._custos.bounds.width + 4 : 0;
    const fullWidth = this._staffRight - this._staffLeft;
    const notationsWidth = sumBy(justificationNotations, (n) => n.bounds.width);

    const lastNotation = notations[notations.length - 1];
    lastNotation.bounds.setX(this._staffRight - lastNotation.bounds.width - custosWidth);

    const freeSpace =
      fullWidth -
      notationsWidth -
      notations[1].bounds.right -
      lastNotation.bounds.width -
      custosWidth;
    const stepWidth = freeSpace / justificationNotations.length;

    for (let i = 2; i < notations.length - 1; i++) {
      const x = notations[i - 1].bounds.right + stepWidth;
      notations[i].bounds.setX(x);
    }
  }
}
